import {AfterViewInit, Component, ElementRef, HostListener, OnDestroy, Optional} from '@angular/core';
import {NgbActiveModal, NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {TemplateVariable} from '../../froala/template-variables';
import {TemplateVariablePickerComponent} from '../template-variable-picker/template-variable-picker.component';
import {conditionExpression, conditionLabel, CONDITION_OPERATORS, newCondition, TemplateCondition,
  ConditionValueType, validCondition} from '../../froala/template-conditions';

/** Adds runtime format hints to values in the shared variable catalog. */
export interface ConditionVariable extends TemplateVariable { valueType?: ConditionValueType; }

/** Returns a validated change while leaving branch content under Froala's control. */
export interface TemplateConditionResult {
  condition?: TemplateCondition;
  unless?: boolean;
  otherwise?: boolean;
  unwrap?: boolean;
}

export interface TemplateConditionEditorComponent extends Translatable {}

/** Edits nested comparisons through readable, typed controls instead of source syntax. */
@Component({
  selector: 'app-qa-template-condition-editor',
  templateUrl: './template-condition-editor.component.html',
  styleUrls: ['./template-condition-editor.component.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class TemplateConditionEditorComponent implements AfterViewInit, OnDestroy {
  private picker: NgbModalRef = null;
  private drag: {pointerId: number; x: number; y: number; handle: HTMLElement} = null;
  private offset = {x: 0, y: 0};
  private resizeObserver: ResizeObserver;
  public variables: ConditionVariable[] = [];
  public condition: TemplateCondition = newCondition();
  public unless = false;
  public otherwise = false;
  public existing = false;
  public hadOtherwise = false;
  public operators = CONDITION_OPERATORS;
  public comparisons = Object.keys(CONDITION_OPERATORS);
  public types: {id: ConditionValueType; label: string}[] = [
    {id: 'text', label: 'Text'}, {id: 'number', label: 'Zahl'}, {id: 'boolean', label: 'Ja / Nein'},
    {id: 'date', label: 'Datum'}, {id: 'variable', label: 'Variable'}
  ];

  /** Injects the condition dialog and shared searchable variable picker. */
  public constructor(public activeModal: NgbActiveModal, private modals: NgbModal,
    @Optional() private translationService: TranslationService, private element: ElementRef<HTMLElement>) {}

  /** Resolves readable names while preserving legacy patient aliases. */
  public name(path: string): string {
    const variable = this.variables.find(item => item.path === path?.replace(/^patient\./, 'client.'));
    return variable?.label || path || this._('Variable wählen');
  }

  /** Shares the variable picker rather than introducing a second, less capable selector. */
  public choose(rule: TemplateCondition, value = false): void {
    const modal = this.modals.open(TemplateVariablePickerComponent, {
      centered: true, size: 'lg', ariaLabelledBy: 'qa-template-variable-title'
    });
    this.picker = modal;
    modal.componentInstance.variables = this.variables;
    modal.result.then((variable: ConditionVariable) => {
      this.picker = null;
      if (value) { rule.value = variable.path; return; }
      rule.path = variable.path;
      rule.valueType = variable.valueType || 'text';
      rule.value = rule.valueType === 'boolean' ? 'true' : '';
    }, () => this.picker = null);
  }

  /** Re-clamps a moved window when adding rules or validation messages changes its size. */
  public ngAfterViewInit(): void {
    const content = this.element.nativeElement.closest('.modal-content');
    if (!content) { return; }
    this.resizeObserver = new ResizeObserver(() => {
      if (this.offset.x || this.offset.y) { this.constrainWindow(); }
    });
    this.resizeObserver.observe(content);
  }

  /** Closes a nested variable picker when its owning editor is destroyed. */
  public ngOnDestroy(): void {
    this.picker?.dismiss();
    this.resizeObserver?.disconnect();
    this.endDrag();
  }

  /** Captures dragging on the header while keeping close buttons and other controls interactive. */
  public startDrag(event: PointerEvent): void {
    if (event.button !== 0 || !event.isPrimary || (event.target as Element).closest('button, input, select')) { return; }
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);
    this.drag = {pointerId: event.pointerId, x: event.clientX, y: event.clientY, handle};
    event.preventDefault();
  }

  /** Moves the actual window within viewport edges, independent of the centered dialog's full-height wrapper. */
  @HostListener('document:pointermove', ['$event'])
  public moveDrag(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) { return; }
    const dx = event.clientX - this.drag.x;
    const dy = event.clientY - this.drag.y;
    this.drag.x = event.clientX;
    this.drag.y = event.clientY;
    this.moveWindow(dx, dy);
  }

  /** Releases pointer capture on completion or cancellation. */
  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  public endDrag(): void {
    if (this.drag?.handle.hasPointerCapture(this.drag.pointerId)) {
      this.drag.handle.releasePointerCapture(this.drag.pointerId);
    }
    this.drag = null;
  }

  /** Keeps a previously moved dialog visible when the browser window becomes smaller. */
  @HostListener('window:resize')
  public constrainWindow(): void { this.moveWindow(0, 0); }

  /** Clamps movement with a small edge gap and leaves tall content scrollable inside the window. */
  private moveWindow(dx: number, dy: number): void {
    const content = this.element.nativeElement.closest('.modal-content') as HTMLElement;
    if (!content) { return; }
    const bounds = content.getBoundingClientRect();
    const gap = 8;
    this.offset.x += Math.max(gap - bounds.left, Math.min(dx, window.innerWidth - gap - bounds.right));
    this.offset.y += Math.max(gap - bounds.top, Math.min(dy, window.innerHeight - gap - bounds.bottom));
    content.style.transform = `translate(${this.offset.x}px, ${this.offset.y}px)`;
  }

  /** Explains text characters that cannot round-trip through both HTML and Handlebars string literals. */
  public get invalidText(): boolean {
    /** Checks only literal text operands because paths and numeric values have separate validation. */
    const invalid = (rule: TemplateCondition): boolean => rule.kind === 'group' ? rule.children.some(invalid) :
      this.needsValue(rule) && rule.valueType === 'text' && /[{}<>&\\\r\n]/.test(rule.value || '');
    return invalid(this.condition);
  }

  /** Clears incompatible values when the user deliberately changes the comparison type. */
  public changeType(rule: TemplateCondition): void {
    rule.value = rule.valueType === 'boolean' ? 'true' : '';
  }

  /** Indicates whether an operator needs a right-hand value. */
  public needsValue(rule: TemplateCondition): boolean {
    return ['truthy', 'present', 'empty'].indexOf(rule.operator) === -1;
  }

  /** Appends a blank rule or a nested group without altering the existing rows. */
  public add(group: TemplateCondition, nested = false): void {
    group.children.push(nested ? {kind: 'group', operator: 'and', children: [newCondition()]} : newCondition());
  }

  /** Wraps a single condition so a second rule can be combined with it. */
  public combine(): void {
    this.condition = {kind: 'group', operator: 'and', children: [this.condition, newCondition()]};
  }

  /** Checks the entire tree, including empty groups and typed values. */
  public get valid(): boolean { return validCondition(this.condition); }

  /** Shows the exact expression for users familiar with existing Handlebars templates. */
  public get source(): string {
    return this.valid ? `{{#${this.unless ? 'unless' : 'if'} ${conditionExpression(this.condition)}}}` : '';
  }

  /** Summarizes the complete condition in the administration language. */
  public get summary(): string {
    const labels: {[path: string]: string} = {};
    this.variables.forEach(variable => labels[variable.path] = variable.label);
    return conditionLabel(this.condition, labels, text => this._(text));
  }

  /** Returns a validated condition while retaining any existing alternative branch. */
  public save(): void {
    if (!this.valid) { return; }
    this.activeModal.close({condition: this.condition, unless: this.unless, otherwise: this.otherwise} as TemplateConditionResult);
  }

  /** Stops global save shortcuts from saving the questionnaire behind an open dialog. */
  @HostListener('keydown', ['$event'])
  public keydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}
